import { useRef, useEffect, useLayoutEffect, useState } from "react";
import { ArrowUpRight, Sparkles, Timer } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { DeviceFrameDesktop, DeviceFramePhone } from "@/components/DeviceFrames";
import { useLang } from "@/i18n/translations";
import { gsap, usePrefersReducedMotion } from "@/hooks/useSmoothScroll";
import { HDH_BEAT_ASSETS, PROJECTS } from "@/lib/siteConfig";

const HDH = PROJECTS.find((p) => p.id === "hausdeshandys");
const RED = "#ef4444"; // dezente Projektfarbe — CloudService bleibt dominant

const useIsDesktop = () => {
  const [d, setD] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  useEffect(() => {
    const fn = () => setD(window.innerWidth >= 1024);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return d;
};

// Lazy autoplay video: loads + plays only when visible, pauses offscreen.
// MP4 (H.264) first for Safari, WebM (VP9) fallback for builds without H.264.
const LazyVideo = ({ src, webm, poster, width, height, className = "", testId }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          if (v.preload === "none") v.preload = "auto";
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);
  return (
    <video
      ref={ref}
      data-testid={testId}
      poster={poster}
      width={width}
      height={height}
      muted
      loop
      playsInline
      preload="none"
      className={`block w-full h-auto ${className}`}
    >
      <source src={src} type="video/mp4" />
      {webm && <source src={webm} type="video/webm" />}
    </video>
  );
};

const Kicker = ({ children }) => (
  <p className="flex items-center gap-2 text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">
    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: RED }} aria-hidden="true" />
    {children}
  </p>
);

// img with intrinsic dimensions — prevents lazy-load layout shift
const Shot = ({ asset, eager = false, className = "" }) => (
  <img
    src={asset.src}
    alt={asset.alt}
    width={asset.w}
    height={asset.h}
    loading={eager ? "eager" : "lazy"}
    className={`block w-full h-auto ${className}`}
  />
);

/* ============ Beats 2+3 — Konfigurator-Stepper (Desktop pinned) ============ */
/* WICHTIG (Stabilität): Stacked/Pinned als separate Komponenten + useLayoutEffect,
   damit ctx.revert() (Unpin) vor der DOM-Entfernung läuft — verhindert
   removeChild-Crash (schwarze Seite) bei Viewport-/Breakpoint-Wechsel. */
const ConfiguratorStepperStacked = () => {
  const { t } = useLang();
  const steps = HDH_BEAT_ASSETS.configuratorSteps;
  return (
    <div className="px-4 sm:px-6" data-testid="hdh-stepper-stacked">
      <div className="max-w-[1200px] mx-auto">
        <Reveal>
          <Kicker>{t.hdh.stepKicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.hdh.stepTitle}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.hdh.stepText}</p>
        </Reveal>
        <Reveal delay={0.1} className="mt-8">
          <DeviceFrameDesktop url="hausdeshandys.at" testId="hdh-stacked-brands">
            <Shot asset={steps[0]} />
          </DeviceFrameDesktop>
        </Reveal>
        <Reveal delay={0.1} className="mt-10">
          <h2 className="font-display text-2xl font-semibold tracking-[-0.02em] text-foreground">{t.hdh.smartTitle}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.hdh.smartText}</p>
        </Reveal>
        <Reveal delay={0.1} className="mt-8">
          <DeviceFrameDesktop url="hausdeshandys.at" testId="hdh-stacked-price">
            <Shot asset={steps[3]} />
          </DeviceFrameDesktop>
        </Reveal>
      </div>
    </div>
  );
};

const ConfiguratorStepperPinned = () => {
  const { t } = useLang();
  const sec = useRef(null);
  const imgRefs = useRef([]);
  const [active, setActive] = useState(0);
  const activeRef = useRef(0);
  const steps = HDH_BEAT_ASSETS.configuratorSteps;

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const imgs = imgRefs.current.filter(Boolean);
      gsap.set(imgs.slice(1), { autoAlpha: 0 });
      gsap.set(imgs[0], { autoAlpha: 1 });
      const tl = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
          trigger: sec.current,
          start: "top top",
          end: "+=260%",
          pin: true,
          scrub: 1,
          anticipatePin: 1,
          onUpdate: (self) => {
            const idx = Math.min(3, Math.floor(self.progress * 4));
            if (idx !== activeRef.current) {
              activeRef.current = idx;
              setActive(idx);
            }
          },
        },
      });
      tl.to({}, { duration: 4 });
      imgs.forEach((img, i) => {
        if (i === 0) return;
        tl.to(imgs[i - 1], { autoAlpha: 0, scale: 1.015, duration: 0.3, ease: "power1.inOut" }, i - 0.15);
        tl.fromTo(img, { autoAlpha: 0, scale: 0.99 }, { autoAlpha: 1, scale: 1, duration: 0.3, ease: "power1.inOut" }, i - 0.15);
      });
    }, sec);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={sec} data-testid="hdh-stepper-pinned" className="relative h-screen flex items-center overflow-hidden">
      <div className="max-w-[1280px] w-full mx-auto px-6 lg:px-10 grid grid-cols-12 gap-10 items-center">
        <div className="col-span-4">
          <Kicker>{t.hdh.stepKicker}</Kicker>
          <div className={`mt-4 transition-opacity duration-300 ${active < 2 ? "opacity-100" : "opacity-35"}`} data-testid="hdh-step-copy-a">
            <h2 className="font-display text-2xl xl:text-3xl font-semibold tracking-[-0.02em] text-foreground leading-tight">{t.hdh.stepTitle}</h2>
            <p className="mt-2.5 text-sm xl:text-base text-muted-foreground leading-relaxed">{t.hdh.stepText}</p>
          </div>
          <div className={`mt-6 transition-opacity duration-300 ${active >= 2 ? "opacity-100" : "opacity-35"}`} data-testid="hdh-step-copy-b">
            <h2 className="font-display text-2xl xl:text-3xl font-semibold tracking-[-0.02em] text-foreground leading-tight">{t.hdh.smartTitle}</h2>
            <p className="mt-2.5 text-sm xl:text-base text-muted-foreground leading-relaxed">{t.hdh.smartText}</p>
          </div>
          {/* Stepper */}
          <ol className="mt-8 space-y-3" data-testid="hdh-stepper-list">
            {t.hdh.steps.map((s, i) => (
              <li key={s.num} className="flex items-center gap-3">
                <span
                  className={`w-8 h-8 rounded-full border flex items-center justify-center text-[11px] font-mono-cs transition-[background-color,border-color,color,box-shadow] duration-300 ${
                    active === i
                      ? "text-white border-transparent"
                      : active > i
                      ? "text-foreground/70 border-white/20"
                      : "text-muted-foreground/50 border-white/10"
                  }`}
                  style={active === i ? { backgroundColor: RED, boxShadow: "0 0 16px rgba(239,68,68,0.45)" } : undefined}
                >
                  {s.num}
                </span>
                <span className={`text-sm transition-colors duration-300 ${active === i ? "text-foreground font-medium" : "text-muted-foreground/60"}`}>
                  {s.label}
                </span>
              </li>
            ))}
          </ol>
        </div>
        <div className="col-span-8">
          <DeviceFrameDesktop url="hausdeshandys.at" testId="hdh-stepper-frame">
            <div className="relative" style={{ aspectRatio: "1920/1142" }}>
              {steps.map((s, i) => (
                <img
                  key={s.src}
                  ref={(el) => (imgRefs.current[i] = el)}
                  src={s.src}
                  alt={s.alt}
                  width={s.w}
                  height={s.h}
                  loading={i === 0 ? "eager" : "lazy"}
                  className="absolute inset-0 w-full h-full object-cover will-change-transform"
                  data-testid={`hdh-step-screen-${i}`}
                />
              ))}
            </div>
          </DeviceFrameDesktop>
        </div>
      </div>
    </div>
  );
};

const ConfiguratorStepper = () => {
  const reduced = usePrefersReducedMotion();
  const isDesktop = useIsDesktop();
  return !isDesktop || reduced ? <ConfiguratorStepperStacked /> : <ConfiguratorStepperPinned />;
};

/* ============ Hauptsektion ============ */
export const HausDesHandysCaseStudy = () => {
  const { t } = useLang();

  return (
    <section
      id="case-hausdeshandys"
      data-testid="case-study-hausdeshandys"
      className="relative py-20 sm:py-28 bg-[#070a12] border-y border-white/5 overflow-hidden"
    >
      {/* dezenter roter Glow — Projektfarbe respektiert, CloudService dominant */}
      <div
        aria-hidden="true"
        className="absolute top-[2%] right-[-10%] w-[560px] h-[560px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(239,68,68,0.07), transparent 70%)" }}
      />

      {/* ---- Header ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <Kicker>{t.hdh.kicker}</Kicker>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.025em] leading-[1.08] text-foreground max-w-3xl">
            {t.hdh.headline}
          </h1>
          <p className="mt-5 text-base lg:text-lg text-muted-foreground leading-relaxed max-w-2xl">{t.hdh.intro}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {HDH.allBadges.map((b) => (
              <Badge key={b} variant="outline" className="bg-white/5 border-white/10 text-foreground/80 font-mono-cs text-[10px] font-normal rounded-full px-2.5">
                {b}
              </Badge>
            ))}
          </div>
          <a
            data-testid="hdh-visit-link"
            href={HDH.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-[#00d4ff] hover:text-[#33ddff] transition-colors duration-200"
          >
            {t.hdh.visit} <ArrowUpRight className="w-4 h-4" />
          </a>
        </Reveal>
      </div>

      {/* ---- Beat 1 — Premium Shop Experience ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-16" data-testid="hdh-beat-1">
        <Reveal>
          <Kicker>{t.hdh.b1Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.hdh.b1Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.hdh.b1Text}</p>
        </Reveal>
        <Reveal delay={0.12} className="mt-9">
          <div className="relative">
            <DeviceFrameDesktop url="hausdeshandys.at" testId="hdh-hero-frame">
              <Shot asset={HDH_BEAT_ASSETS.shopHeroDesktop} eager />
            </DeviceFrameDesktop>
            <div className="absolute -bottom-10 -right-2 sm:right-8 w-[24%] max-w-[170px]">
              <DeviceFramePhone testId="hdh-hero-phone">
                <Shot asset={HDH_BEAT_ASSETS.shopHeroMobile} />
              </DeviceFramePhone>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <DeviceFrameDesktop url="hausdeshandys.at" testId="hdh-pickup-frame">
              <Shot asset={HDH_BEAT_ASSETS.shopPickup} />
            </DeviceFrameDesktop>
          </div>
          <p className="lg:col-span-5 text-sm text-muted-foreground leading-relaxed font-mono-cs">{t.hdh.b1PickupNote}</p>
        </Reveal>
      </div>

      {/* ---- Beats 2+3 — Konfigurator (pinned Stepper) ---- */}
      <div className="mt-24" data-testid="hdh-beat-2-3">
        <ConfiguratorStepper />
      </div>

      {/* ---- Beat 3 Zusatz — Express Slot ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-20" data-testid="hdh-express">
        <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-4 order-2 lg:order-1">
            <p className="flex items-center gap-2 text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">
              <Timer className="w-3.5 h-3.5" style={{ color: RED }} />
              Express
            </p>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-[-0.02em] text-foreground">{t.hdh.expressTitle}</h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{t.hdh.expressText}</p>
          </div>
          <div className="lg:col-span-8 order-1 lg:order-2">
            <DeviceFrameDesktop url="hausdeshandys.at" testId="hdh-express-frame">
              <Shot asset={HDH_BEAT_ASSETS.express} />
            </DeviceFrameDesktop>
          </div>
        </Reveal>
      </div>

      {/* ---- Beat 4 — AI Shopping Assistant (Chat-Moment) ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="hdh-beat-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5">
            <p className="flex items-center gap-2 text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">
              <Sparkles className="w-3.5 h-3.5" style={{ color: RED }} />
              {t.hdh.b4Kicker}
            </p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.hdh.b4Title}</h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{t.hdh.b4Text}</p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7 flex justify-center">
            <div className="relative w-[250px] sm:w-[290px]">
              <div
                aria-hidden="true"
                className="absolute -inset-12 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(239,68,68,0.10), transparent 70%)" }}
              />
              <DeviceFramePhone testId="hdh-assistant-phone">
                <Shot asset={HDH_BEAT_ASSETS.aiAssistant} />
              </DeviceFramePhone>
            </div>
          </Reveal>
        </div>
      </div>

      {/* ---- Beat 5 — Warenkorb & Checkout ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="hdh-beat-5">
        <Reveal>
          <Kicker>{t.hdh.b5Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.hdh.b5Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.hdh.b5Text}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal delay={0.08} className="lg:col-span-4 flex justify-center lg:justify-start">
            <div className="w-[230px] sm:w-[260px]">
              <DeviceFramePhone testId="hdh-cart-phone">
                <Shot asset={HDH_BEAT_ASSETS.cart} />
              </DeviceFramePhone>
            </div>
          </Reveal>
          <Reveal delay={0.16} className="lg:col-span-8">
            <DeviceFrameDesktop url="hausdeshandys.at" testId="hdh-checkout-frame">
              <Shot asset={HDH_BEAT_ASSETS.checkout} />
            </DeviceFrameDesktop>
          </Reveal>
        </div>
      </div>

      {/* ---- Beat 6 — Admin & Betrieb ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="hdh-beat-6">
        <Reveal>
          <Kicker>{t.hdh.b6Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.hdh.b6Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.hdh.b6Text}</p>
        </Reveal>
        <Reveal delay={0.12} className="mt-9">
          <div className="relative">
            <DeviceFrameDesktop url="hausdeshandys.at/admin" testId="hdh-admin-frame">
              <Shot asset={HDH_BEAT_ASSETS.adminDesktop} />
            </DeviceFrameDesktop>
            <div className="absolute -bottom-10 left-2 sm:left-8 w-[22%] max-w-[160px]">
              <DeviceFramePhone testId="hdh-admin-phone">
                <Shot asset={HDH_BEAT_ASSETS.adminMobile} />
              </DeviceFramePhone>
            </div>
          </div>
        </Reveal>
      </div>

      {/* ---- Live Flow — Videos ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-28" data-testid="hdh-flow">
        <Reveal>
          <Kicker>{t.hdh.flowKicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.hdh.flowTitle}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.hdh.flowText}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal delay={0.08} className="lg:col-span-8">
            <DeviceFrameDesktop url="hausdeshandys.at" testId="hdh-flow-desktop-frame">
              <LazyVideo
                src={HDH_BEAT_ASSETS.flowDesktop.src}
                webm={HDH_BEAT_ASSETS.flowDesktop.webm}
                poster={HDH_BEAT_ASSETS.flowDesktop.poster}
                width={HDH_BEAT_ASSETS.flowDesktop.w}
                height={HDH_BEAT_ASSETS.flowDesktop.h}
                testId="hdh-flow-desktop-video"
              />
            </DeviceFrameDesktop>
          </Reveal>
          <Reveal delay={0.16} className="lg:col-span-4 flex justify-center">
            <div className="w-[230px] sm:w-[260px]">
              <DeviceFramePhone testId="hdh-flow-phone-frame">
                <LazyVideo
                  src={HDH_BEAT_ASSETS.flowMobile.src}
                  webm={HDH_BEAT_ASSETS.flowMobile.webm}
                  poster={HDH_BEAT_ASSETS.flowMobile.poster}
                  width={HDH_BEAT_ASSETS.flowMobile.w}
                  height={HDH_BEAT_ASSETS.flowMobile.h}
                  testId="hdh-flow-mobile-video"
                />
              </DeviceFramePhone>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
